import React from 'react';
import { Modal, ModalProps } from 'antd';
import { WxbButton } from '../Button/Button';
import { WxbIcon } from '../Icon/Icon';
import { resolvePortalContainer } from '../_internal/portalContainer';
import {
  clampWxbModalSize,
  getWxbModalInitialResizeSize,
  getWxbModalResizeBounds,
} from './modalResize';
import type { WxbModalResizeLimits, WxbModalSize } from './modalResize';
import './WxbModal.css';

type WxbModalResizeMode = 'right' | 'bottom' | 'corner';

interface WxbModalResizeState {
  mode: WxbModalResizeMode;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

export interface WxbModalProps extends ModalProps {
  okVariant?: 'primary' | 'danger';
  resizable?: boolean;
  resizeDefaultSize?: Partial<WxbModalSize>;
  resizeLimits?: WxbModalResizeLimits;
  onResize?: (size: WxbModalSize) => void;
}

const RESIZE_KEY_STEP = 20;
const RESIZE_KEY_STEP_LARGE = 40;

const joinClasses = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

export const WxbModal: React.FC<WxbModalProps> = ({
  children,
  className = '',
  rootClassName = '',
  wrapClassName = '',
  title,
  onOk,
  onCancel,
  okText = '确定',
  cancelText = '取消',
  confirmLoading,
  footer,
  closeIcon,
  okVariant = 'primary',
  resizable = false,
  resizeDefaultSize,
  resizeLimits,
  onResize,
  modalRender,
  width,
  okButtonProps,
  cancelButtonProps,
  // 默认挂进当前全屏元素(无全屏时为 document.body),避免全屏下被 top layer 遮挡;
  // 调用方显式传 getContainer 则尊重其值。
  getContainer = resolvePortalContainer,
  ...props
}) => {
  const shellRef = React.useRef<HTMLDivElement | null>(null);
  const resizeStateRef = React.useRef<WxbModalResizeState | null>(null);
  const wasOpenRef = React.useRef(false);
  const [resizableSize, setResizableSize] = React.useState<WxbModalSize>(() => (
    getWxbModalInitialResizeSize(width, resizeDefaultSize, resizeLimits)
  ));
  const [isResizing, setIsResizing] = React.useState(false);
  const isOpen = Boolean((props as { open?: boolean; visible?: boolean }).open ?? (props as { visible?: boolean }).visible);
  const resizeBounds = getWxbModalResizeBounds(resizeLimits);

  const setClampedResizableSize = React.useCallback((nextSize: WxbModalSize) => {
    const nextBounds = getWxbModalResizeBounds(resizeLimits);
    const clampedSize = clampWxbModalSize(nextSize, nextBounds);

    setResizableSize((currentSize) => {
      if (currentSize.width === clampedSize.width && currentSize.height === clampedSize.height) {
        return currentSize;
      }
      return clampedSize;
    });

    onResize?.(clampedSize);
  }, [onResize, resizeLimits]);

  React.useEffect(() => {
    if (!resizable || !isOpen || wasOpenRef.current) {
      wasOpenRef.current = isOpen;
      return;
    }

    const initialSize = getWxbModalInitialResizeSize(width, resizeDefaultSize, resizeLimits);
    setResizableSize(initialSize);
    wasOpenRef.current = true;
  }, [isOpen, resizable, resizeDefaultSize, resizeLimits, width]);

  React.useEffect(() => {
    if (!resizable || !isOpen || typeof window === 'undefined') return undefined;

    const handleWindowResize = () => {
      setResizableSize((currentSize) => clampWxbModalSize(
        currentSize,
        getWxbModalResizeBounds(resizeLimits),
      ));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isOpen, resizable, resizeLimits]);

  React.useEffect(() => {
    if (!resizable || !shellRef.current) return;
    shellRef.current.style.setProperty('--wxb-modal-resizable-height', `${resizableSize.height}px`);
    shellRef.current.style.setProperty('--wxb-modal-resizable-max-height', `${resizeBounds.maxHeight}px`);
  }, [resizable, resizeBounds.maxHeight, resizableSize.height]);

  React.useEffect(() => {
    if (!isResizing || typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    document.body.classList.add('wxb-modal-is-resizing');

    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      const deltaX = event.clientX - resizeState.startX;
      const deltaY = event.clientY - resizeState.startY;

      setClampedResizableSize({
        width: resizeState.mode === 'right' || resizeState.mode === 'corner'
          ? resizeState.startWidth + deltaX
          : resizeState.startWidth,
        height: resizeState.mode === 'bottom' || resizeState.mode === 'corner'
          ? resizeState.startHeight + deltaY
          : resizeState.startHeight,
      });
    };

    const stopResize = () => {
      resizeStateRef.current = null;
      setIsResizing(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize, { once: true });
    window.addEventListener('pointercancel', stopResize, { once: true });

    return () => {
      document.body.classList.remove('wxb-modal-is-resizing');
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };
  }, [isResizing, resizable, setClampedResizableSize]);

  const beginResize = React.useCallback((
    event: React.PointerEvent<HTMLElement>,
    mode: WxbModalResizeMode,
  ) => {
    if (!resizable) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeStateRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: resizableSize.width,
      startHeight: resizableSize.height,
    };
    setIsResizing(true);
  }, [resizable, resizableSize]);

  const handleResizeKeyDown = React.useCallback((
    event: React.KeyboardEvent<HTMLElement>,
    mode: WxbModalResizeMode,
  ) => {
    const step = event.shiftKey ? RESIZE_KEY_STEP_LARGE : RESIZE_KEY_STEP;
    let widthDelta = 0;
    let heightDelta = 0;

    if ((mode === 'right' || mode === 'corner') && event.key === 'ArrowRight') widthDelta = step;
    if ((mode === 'right' || mode === 'corner') && event.key === 'ArrowLeft') widthDelta = -step;
    if ((mode === 'bottom' || mode === 'corner') && event.key === 'ArrowDown') heightDelta = step;
    if ((mode === 'bottom' || mode === 'corner') && event.key === 'ArrowUp') heightDelta = -step;

    if (widthDelta === 0 && heightDelta === 0) return;

    event.preventDefault();
    setClampedResizableSize({
      width: resizableSize.width + widthDelta,
      height: resizableSize.height + heightDelta,
    });
  }, [resizableSize, setClampedResizableSize]);

  const renderResizeHandle = (
    mode: WxbModalResizeMode,
    label: string,
    orientation?: 'horizontal' | 'vertical',
  ) => (
    <span
      className={`wxb-modal-resize-handle wxb-modal-resize-handle-${mode}`}
      role="separator"
      aria-label={label}
      aria-orientation={orientation}
      aria-valuemin={mode === 'bottom' ? resizeBounds.minHeight : resizeBounds.minWidth}
      aria-valuemax={mode === 'bottom' ? resizeBounds.maxHeight : resizeBounds.maxWidth}
      aria-valuenow={mode === 'bottom' ? resizableSize.height : resizableSize.width}
      tabIndex={0}
      onPointerDown={(event) => beginResize(event, mode)}
      onKeyDown={(event) => handleResizeKeyDown(event, mode)}
    />
  );

  const renderResizableModal = (modal: React.ReactNode) => {
    const renderedModal = modalRender ? modalRender(modal) : modal;
    if (!resizable) return renderedModal;

    return (
      <div
        ref={shellRef}
        className={joinClasses('wxb-modal-resizable-shell', isResizing && 'wxb-modal-resizable-shell-resizing')}
      >
        {renderedModal}
        {renderResizeHandle('right', '拖拽调整弹窗宽度', 'vertical')}
        {renderResizeHandle('bottom', '拖拽调整弹窗高度', 'horizontal')}
        {renderResizeHandle('corner', '拖拽调整弹窗宽度和高度')}
      </div>
    );
  };

  const customFooter = footer === undefined ? (
    <div className="wxb-modal-footer">
      <WxbButton
        variant="ghost"
        className={cancelButtonProps?.className}
        disabled={cancelButtonProps?.disabled}
        onClick={onCancel as any}
      >
        {cancelText}
      </WxbButton>
      <WxbButton
        variant={okVariant === 'danger' || okButtonProps?.danger ? 'danger' : okVariant}
        className={okButtonProps?.className}
        onClick={onOk as any}
        disabled={Boolean(confirmLoading || okButtonProps?.disabled)}
        aria-busy={confirmLoading || undefined}
      >
        {confirmLoading ? '处理中...' : okText}
      </WxbButton>
    </div>
  ) : footer;

  const customTitle = title ? (
    <h2 className="wxb-h4 wxb-modal-title">{title}</h2>
  ) : undefined;

  return (
    <Modal
      className={joinClasses('wxb-modal', resizable && 'wxb-modal-resizable', isResizing && 'wxb-modal-resizing', className)}
      rootClassName={joinClasses('wxb-modal-root', rootClassName)}
      wrapClassName={joinClasses('wxb-modal-wrap', resizable && 'wxb-modal-wrap-resizable', wrapClassName)}
      title={customTitle}
      footer={customFooter}
      getContainer={getContainer}
      onCancel={onCancel}
      modalRender={renderResizableModal}
      width={resizable ? resizableSize.width : width}
      closeIcon={closeIcon ?? (
        <span className="wxb-modal-close-icon" aria-label="关闭">
          <WxbIcon name="close" size={18} />
        </span>
      )}
      {...props}
    >
      {children}
    </Modal>
  );
};
